import React, {useCallback, useEffect, useMemo, useState} from "react";
import {DataTable} from "primereact/datatable";
import {Column} from "primereact/column";


import {IndexTemplate, Loading} from "../../utils/InlineComponents";
import {AppContext} from "../../HomePage";
import {toDateTimeString} from "../../utils/TimeUtil";
import {DashboardService} from "../../service/DashboardService";
import {getHostName} from "../../utils/Utils";
import {Button} from "primereact/button";
import SiteDefinitionAdd from "./SiteDefinitionAdd";
import {InputSwitch} from "primereact/inputswitch";
import {SelectButton} from "primereact/selectbutton";
import {Dialog} from "primereact/dialog";
import {Dropdown} from "primereact/dropdown";
import {AiJobService} from "../../service/AiJobService";
import {requestProbeBaseSpiderOptions, requestProbeProxyOptions, RequestProbeService} from "../../service/RequestProbeService";
import {proxy_countries} from "../../utils/Constants";
import {MultiSelect} from "primereact/multiselect";

const NewWebsites = (props) => {

    const {t, toast, setError} = React.useContext(AppContext);

    const [items, setItems] = useState([]);

    const [loading, setLoading] = useState(false);

    const [showAddSiteDefinition, setShowAddSiteDefinition] = useState(false);
    const [selectedWebSite, setSelectedWebSite] = useState(null);
    const [showAiJobDialog, setShowAiJobDialog] = useState(false);
    const [selectedAiWebsite, setSelectedAiWebsite] = useState(null);
    const [showProbeDialog, setShowProbeDialog] = useState(false);
    const [selectedProbeWebsite, setSelectedProbeWebsite] = useState(null);
    const [selectedProbeSampleUrl, setSelectedProbeSampleUrl] = useState(null);
    const [addWebsiteOperation, setAddWebsiteOperation] = useState(null);
    const [operationsLoading, setOperationsLoading] = useState(false);
    const [selectedProvider, setSelectedProvider] = useState(null);
    const [selectedModel, setSelectedModel] = useState(null);
    const [aiJobsByWebsiteId, setAiJobsByWebsiteId] = useState({});
    const [aiJobLoading, setAiJobLoading] = useState(false);
    const [aiJobCreateLoading, setAiJobCreateLoading] = useState(false);
    const [aiJobError, setAiJobError] = useState(null);
    const [probeProxyCountry, setProbeProxyCountry] = useState("");
    const [selectedProbeRequestType, setSelectedProbeRequestType] = useState("product_url");
    const [selectedProbeProxies, setSelectedProbeProxies] = useState([]);
    const [selectedProbeBaseSpiders, setSelectedProbeBaseSpiders] = useState([]);
    const [probeStates, setProbeStates] = useState({});

    const options = [
        {label: t('actions.active'), value: true},
        {label: t('actions.passive'), value: false}
    ];

    const [activeItems, setActiveItems] = useState(true);

    const dashboardService = useMemo(() => new DashboardService(), []);
    const aiJobService = useMemo(() => new AiJobService(), []);
    const requestProbeService = useMemo(() => new RequestProbeService(), []);
    const proxyCountryOptions = proxy_countries(t);
    const probeProxyOptions = requestProbeProxyOptions.map(option => ({
        label: option.label,
        value: option.name
    }));
    const probeBaseSpiderOptions = requestProbeBaseSpiderOptions.map(option => ({
        label: option.name,
        value: option.name
    }));
    const probeRequestTypeOptions = [
        {label: "product_url", value: "product_url"},
        {label: "product_list", value: "product_list"},
        {label: "site_search", value: "site_search"}
    ];

    const activeAiJobStatuses = [
        "queued",
        "collecting_sample_responses",
        "analyzing_website",
        "generating_spider_change",
        "applying_patch",
        "testing_spider",
        "creating_pull_request",
        "waiting_implementation",
        "waiting_user_review"
    ];

    const getProbeStateKey = useCallback((url, requestType = selectedProbeRequestType) => {
        return (requestType || "product_url") + "::" + url;
    }, [selectedProbeRequestType]);

    const updateAiJobMap = useCallback((jobs) => {
        setAiJobsByWebsiteId(currentMap => {
            const nextMap = {...currentMap};
            (jobs || []).forEach(job => {
                const undefinedWebsiteId = job && job.input ? job.input.undefined_website_id : null;
                if (!undefinedWebsiteId)
                    return;
                const existingJob = nextMap[undefinedWebsiteId];
                if (!existingJob || new Date(job.updated_at || job.created_at || 0) > new Date(existingJob.updated_at || existingJob.created_at || 0))
                    nextMap[undefinedWebsiteId] = job;
            });
            return nextMap;
        });
    }, []);

    const loadAddWebsiteJobs = useCallback(() => {
        aiJobService.listJobs({operation: "add-website-spider", limit: 200}).then(updateAiJobMap).catch(() => {});
    }, [aiJobService, updateAiJobMap]);

    const search = useCallback(() => {
        setLoading(true);
        dashboardService.getUndefinedWebsites().then(data=>{
            setItems(data);
            setLoading(false);
            loadAddWebsiteJobs();
        }).catch(error =>{
            setError(error);
            setError(null);
            setLoading(false);
        });
    }, [dashboardService, loadAddWebsiteJobs, setError]);

    const normalizeOperations = (data) => {
        if (Array.isArray(data))
            return data;
        if (data && Array.isArray(data.data))
            return data.data;
        if (data && data.providers)
            return [{domain: "scraper", operation: "add-website-spider", repo: "pricescraper", providers: data.providers}];
        return [];
    };

    function getDefaultModelName(provider) {
        if (!provider)
            return null;
        if (provider.default_model)
            return provider.default_model;
        if (provider.models && provider.models.length > 0)
            return provider.models[0].name;
        return null;
    }

    const selectDefaultProviderAndModel = useCallback((operation) => {
        const providers = operation && operation.providers ? operation.providers : [];
        const implementedProviders = providers.filter(provider => provider.is_implemented !== false);
        const geminiProvider = implementedProviders.find(provider => provider.name === "gemini");
        const provider = geminiProvider || implementedProviders[0] || null;
        setSelectedProvider(provider ? provider.name : null);
        setSelectedModel(getDefaultModelName(provider));
    }, []);

    const loadAiOperations = useCallback(() => {
        setOperationsLoading(true);
        aiJobService.getOperations().then(data => {
            const operation = normalizeOperations(data).find(item => item.domain === "scraper" && item.operation === "add-website-spider");
            setAddWebsiteOperation(operation || null);
            selectDefaultProviderAndModel(operation);
            setOperationsLoading(false);
        }).catch(error => {
            setOperationsLoading(false);
            setError(error);
            setError(null);
        });
    }, [aiJobService, selectDefaultProviderAndModel, setError]);

    useEffect ( ()=>{
        search();
        loadAiOperations();
    }, [search, loadAiOperations]);

    const dateTemplate = (rowData) => <span>{toDateTimeString(rowData.created_date)}</span>;

    const getSampleProductUrls = (rowData) => {
        const urls = [];
        if (rowData.sample_product_url)
            urls.push(rowData.sample_product_url);
        if (Array.isArray(rowData.sample_product_urls))
            rowData.sample_product_urls.forEach(url => {
                if (url && !urls.includes(url))
                    urls.push(url);
            });
        return urls;
    };

    const getProbeWebsiteHost = (url) => {
        try {
            return new URL(url).hostname.replace(/^www\./, "");
        } catch (e) {
            return getHostName(url).replace(/^www\./, "");
        }
    };

    const updateProbeState = (probeKey, updates) => {
        setProbeStates(currentStates => ({
            ...currentStates,
            [probeKey]: {
                ...(currentStates[probeKey] || {}),
                ...updates
            }
        }));
    };

    const getProbeErrorMessage = useCallback((error) => {
        const data = error && error.response && error.response.data ? error.response.data : null;
        return (data && data.error) || (data && data.message) || t('request_probe.request_failed');
    }, [t]);

    const startRequestProbe = (sampleUrl) => {
        sampleUrl = (sampleUrl || "").trim();
        if (!sampleUrl)
            return;
        setSelectedProbeSampleUrl(sampleUrl);
        const requestType = selectedProbeRequestType || "product_url";
        const probeKey = getProbeStateKey(sampleUrl, requestType);
        const currentState = probeStates[probeKey] || {};
        if (currentState.loading || currentState.polling)
            return;

        const website = getProbeWebsiteHost(sampleUrl);
        updateProbeState(probeKey, {
            undefinedWebsiteId: selectedProbeWebsite ? selectedProbeWebsite.id : (selectedAiWebsite ? selectedAiWebsite.id : null),
            selectedSampleUrl: sampleUrl,
            requestType: requestType,
            website: website,
            loading: true,
            existingLoading: false,
            polling: false,
            waiting: false,
            error: null,
            probeResult: null,
            selectedProxy: null,
            selectedResponseHash: null
        });

        requestProbeService.createProbe(sampleUrl, requestType, probeProxyCountry.trim(), {
            selected_proxies: selectedProbeProxies,
            selected_base_spiders: selectedProbeBaseSpiders,
            campaign_id: selectedProbeWebsite ? selectedProbeWebsite.campaign_id : null
        }).then(data => {
            updateProbeState(probeKey, {
                loading: false,
                polling: false,
                waiting: false,
                scrapingRequestId: data.scraping_request_id,
                state: data.state,
                spider: data.spider,
                selectedProxies: selectedProbeProxies,
                selectedBaseSpiders: selectedProbeBaseSpiders
            });
            toast.current.show({severity: 'success', summary: t('request_probe.probe'), detail: t('message_detail.successful')});
        }).catch(error => {
            updateProbeState(probeKey, {loading: false, polling: false, waiting: false, error: getProbeErrorMessage(error)});
            if (error && error.response && error.response.status === 401) {
                setError(error);
                setError(null);
            }
        });
    };

    const getSelectedProviderConfig = () => {
        const providers = addWebsiteOperation && addWebsiteOperation.providers ? addWebsiteOperation.providers : [];
        return providers.find(provider => provider.name === selectedProvider) || null;
    };

    const getProviderOptions = () => {
        const providers = addWebsiteOperation && addWebsiteOperation.providers ? addWebsiteOperation.providers : [];
        return providers.map(provider => ({
            label: provider.is_implemented === false ? provider.name + " (" + t('ai_job.not_implemented') + ")" : provider.name,
            value: provider.name,
            disabled: provider.is_implemented === false
        }));
    };

    const getModelOptions = () => {
        const provider = getSelectedProviderConfig();
        if (!provider || !provider.models)
            return [];
        return provider.models.map(model => ({
            label: model.label || model.name,
            value: model.name
        }));
    };

    const getAiJobErrorMessage = (error) => {
        const data = error && error.response && error.response.data ? error.response.data : null;
        return (data && data.error) ||
            (data && data.data && data.data.error_message) ||
            (data && data.message) ||
            t('ai_job.request_failed');
    };

    const getStatusLabel = (status) => {
        const labels = {
            queued: t('ai_job.status_queued'),
            collecting_sample_responses: t('ai_job.status_collecting_sample_responses'),
            analyzing_website: t('ai_job.status_analyzing_website'),
            generating_spider_change: t('ai_job.status_generating_spider_change'),
            applying_patch: t('ai_job.status_applying_patch'),
            testing_spider: t('ai_job.status_testing_spider'),
            creating_pull_request: t('ai_job.status_creating_pull_request'),
            waiting_implementation: t('ai_job.status_waiting_implementation'),
            waiting_user_review: t('ai_job.waiting_for_review'),
            completed: t('ai_job.completed'),
            failed: t('ai_job.failed'),
            approval_failed: t('ai_job.approval_failed')
        };
        return labels[status] || status || "-";
    };

    const statusTemplate = (job) => {
        if (!job)
            return "-";
        const className = job.status === "completed" ? "p-tag p-tag-success" :
            (job.status === "failed" || job.status === "approval_failed" ? "p-tag p-tag-danger" :
                (job.status === "waiting_user_review" ? "p-tag p-tag-warning" : "p-tag p-tag-info"));
        return <span className={className}>{getStatusLabel(job.status)}</span>;
    };

    const urlTemplate = (rowData) => {

        let l = document.createElement("a");
        l.href = rowData.url;

        return <div >
            <span className="p-column-title">{t('table_headers.site')}</span>
            <a target="_blank" rel="noopener noreferrer" href={rowData.url} title={getHostName(rowData.url)}> {getHostName(rowData.url)} </a>
            {rowData.request_probe_exists &&
                <span className="p-tag p-tag-success" style={{marginLeft: ".5em", whiteSpace: "nowrap"}}>{t('request_probe.probe_exists')}</span>}
        </div>;
    };

    const refreshSelectedWebsiteAiJobs = (website) => {
        if (!website || !website.id)
            return;
        setAiJobLoading(true);
        aiJobService.listJobs({operation: "add-website-spider", undefined_website_id: website.id}).then(jobs => {
            updateAiJobMap(jobs);
            setAiJobLoading(false);
        }).catch(error => {
            setAiJobLoading(false);
            setError(error);
            setError(null);
        });
    };

    const refreshSelectedAiJobDetails = () => {
        const job = getSelectedWebsiteAiJob();
        if (!job)
            return;
        setAiJobLoading(true);
        aiJobService.getJob(job.id).then(data => {
            updateAiJobMap([data]);
            setAiJobLoading(false);
        }).catch(error => {
            setAiJobLoading(false);
            setError(error);
            setError(null);
        });
    };

    const openAiJobDialog = (website) => {
        setSelectedAiWebsite(website);
        setSelectedWebSite(website);
        setAiJobError(null);
        setShowAiJobDialog(true);
        refreshSelectedWebsiteAiJobs(website);
        if (!addWebsiteOperation)
            loadAiOperations();
    };

    const openProbeDialog = (website) => {
        const sampleUrls = getSampleProductUrls(website);
        setSelectedProbeWebsite(website);
        setSelectedWebSite(website);
        setSelectedProbeSampleUrl(sampleUrls.length > 0 ? sampleUrls[0] : null);
        setSelectedProbeRequestType("product_url");
        setProbeProxyCountry("");
        setSelectedProbeProxies([]);
        setSelectedProbeBaseSpiders([]);
        setShowProbeDialog(true);
    };

    const getSelectedWebsiteAiJob = () => {
        if (!selectedAiWebsite)
            return null;
        return aiJobsByWebsiteId[selectedAiWebsite.id] || null;
    };

    const hasActiveAiJob = (job) => {
        return job && activeAiJobStatuses.includes(job.status);
    };

    const createAddWebsiteAiJob = () => {
        const sampleUrls = getSampleProductUrls(selectedAiWebsite || {});
        const existingJob = getSelectedWebsiteAiJob();
        if (hasActiveAiJob(existingJob)) {
            setAiJobError(null);
            return;
        }
        if (sampleUrls.length === 0) {
            setAiJobError(t('ai_job.sample_urls_required'));
            return;
        }
        setAiJobCreateLoading(true);
        setAiJobError(null);
        aiJobService.createAddWebsiteSpiderJob(selectedAiWebsite.id, selectedProvider, "master", selectedModel).then(job => {
            updateAiJobMap([job]);
            toast.current.show({severity: 'success', summary: t('ai_job.created'), detail: t('message_detail.successful')});
            setAiJobCreateLoading(false);
        }).catch(error => {
            if (error && error.response && error.response.status === 401) {
                setError(error);
                setError(null);
            } else {
                setAiJobError(getAiJobErrorMessage(error));
            }
            setAiJobCreateLoading(false);
        });
    };

    const aiJobDialogFooter = () => {
        const sampleUrls = getSampleProductUrls(selectedAiWebsite || {});
        const existingJob = getSelectedWebsiteAiJob();
        return <div>
            <Button label={t('actions.close')} icon="pi pi-times" className="p-button-outlined" onClick={() => setShowAiJobDialog(false)} style={{marginRight: ".5em"}}/>
            <Button label={aiJobCreateLoading ? t('ai_job.creating') : t('ai_job.create_job')}
                    icon={aiJobCreateLoading ? "pi pi-spin pi-spinner" : "pi pi-bolt"}
                    className="p-button-warning"
                    disabled={aiJobCreateLoading || operationsLoading || !selectedProvider || !selectedModel || sampleUrls.length === 0 || hasActiveAiJob(existingJob)}
                    onClick={createAddWebsiteAiJob}/>
        </div>;
    };

    const probeDialogFooter = () => {
        const probeState = selectedProbeSampleUrl ? (probeStates[getProbeStateKey(selectedProbeSampleUrl)] || {}) : {};
        return <div>
            <Button label={t('actions.close')} icon="pi pi-times" className="p-button-outlined" onClick={() => setShowProbeDialog(false)} style={{marginRight: ".5em"}}/>
            <Button label={t('request_probe.probe')}
                    icon={probeState.loading || probeState.polling ? "pi pi-spin pi-spinner" : "pi pi-send"}
                    className="p-button-info"
                    disabled={!selectedProbeSampleUrl || probeState.loading || probeState.polling}
                    onClick={() => startRequestProbe(selectedProbeSampleUrl)}/>
        </div>;
    };

    const probeStateTemplate = (sampleUrl) => {
        const state = probeStates[getProbeStateKey(sampleUrl)] || {};
        return <div style={{marginTop: ".5em"}}>
            {state.scrapingRequestId &&
                <div><b>{t('request_probe.scraping_request_id')}:</b> {state.scrapingRequestId}</div>}
            {state.selectedProxies && state.selectedProxies.length > 0 &&
                <div><b>{t('request_probe.selected_proxies')}:</b> {state.selectedProxies.join(", ")}</div>}
            {state.selectedBaseSpiders && state.selectedBaseSpiders.length > 0 &&
                <div><b>{t('request_probe.selected_base_spiders')}:</b> {state.selectedBaseSpiders.join(", ")}</div>}
            {state.state &&
                <span className="p-tag p-tag-info" style={{marginRight: ".5em"}}>{state.state}</span>}
            {state.waiting &&
                <span className="p-tag p-tag-warning">{t('request_probe.waiting_result')}</span>}
            {state.error &&
                <div style={{marginTop: ".5em"}}><span className="p-tag p-tag-danger" style={{whiteSpace: "normal"}}>{state.error}</span></div>}
            {state.scrapingRequestId &&
                <div style={{marginTop: ".5em"}}><span className="p-tag p-tag-success" style={{whiteSpace: "normal"}}>{t('request_probe.open_monitor_tab')}</span></div>}
        </div>;
    };

    const actionTemplate = (rowData) => {
        const sampleUrls = getSampleProductUrls(rowData);
        return <div style={{whiteSpace: "nowrap"}}>
                <InputSwitch checked={rowData.is_active} onChange={() => changeIsActive(rowData)} tooltipOptions={{position: 'bottom'}} tooltip={rowData.is_active ? t('actions.deactivate') : t('actions.activate') } style={{marginRight:"0.5em"}} />
                <Button icon="pi pi-plus" className="p-button-outlined p-button-success" tooltip={t('actions.edit')} tooltipOptions={{position: 'bottom'}} onClick={() => {
                    setSelectedWebSite(rowData);
                    setShowAddSiteDefinition(true);
                }} style={{marginRight:"0.5em"}}/>
                <Button icon="pi pi-bolt"
                        className="p-button-outlined p-button-warning"
                        tooltip={sampleUrls.length === 0 ? t('ai_job.sample_urls_required') : t('actions.define_with_ai')}
                        tooltipOptions={{position: 'bottom'}}
                        disabled={sampleUrls.length === 0}
                        onClick={() => openAiJobDialog(rowData)}
                        style={{marginRight:"0.5em"}}/>
                <Button icon="pi pi-send"
                        className="p-button-outlined p-button-info"
                        tooltip={t('request_probe.probe')}
                        tooltipOptions={{position: 'bottom'}}
                        onClick={() => openProbeDialog(rowData)}/>
            </div>;
    };

    const changeIsActive = (rowData) => {
        dashboardService.activeOrDeactivateUndefinedWebsite(rowData.id, rowData.is_active, rowData.url).then(() => {
            // let item = items.find(item => item.id === rowData.id)
            // item.is_active = !rowData.is_active;
            let items_ = items.filter(item => item.url === rowData.url);
            items_.forEach(i=>{
                i.is_active = !i.is_active;
            });

            setItems([...items]);
            toast.current.show({severity: 'success', summary: rowData.is_active ? t('product.activated') : t('product.deactivated'), detail: t('message_detail.successful')})
        }).catch(error =>{
            setError(error);
            setError(null);
        });
    };

    return <React.Fragment>

        {loading ?
            <div className="p-col-12" style={{textAlign: 'center'}}>
                <Loading/>
            </div> :

            <div className="p-grid">
                <div className="p-col-12">
                    <SelectButton value={activeItems} optionLabel="label" options={options}
                                  onChange={(e) => {
                                      setActiveItems(e.value)
                                  }}
                                  style={{marginRight: '.5em', float: 'right'}}/>
                </div>
                <div className="p-col-12">
                    <DataTable value={items.filter(item => item.is_active === activeItems)}
                               selectionMode="single"
                               selection={selectedWebSite}
                               onSelectionChange={(e) =>{setSelectedWebSite(e.value)}}
                               emptyMessage={t('actions.empty_message')} paginator={true} rows={100}
                               sortField="created_date" sortOrder={-1}
                               rowGroupMode="rowspan"
                               groupRowsBy="url">
                        <Column body={IndexTemplate} style={{width: '3em'}}/>
                        <Column field="url" body={urlTemplate} style={{width: '20em'}} header={t('scraping_request.hostname')} sortable filter={true} filterMatchMode="contains" group/>
                        <Column field="user_email" style={{width: '18em', minWidth: '18em'}} header={t('user.email')} sortable filter={true} filterMatchMode="contains"/>
                        <Column field="used_base_spider" style={{width: '15em'}} header={t('campaign.competitors.base_spider')} sortable filter={true} filterMatchMode="contains"/>
                        <Column field="created_date" body={dateTemplate} header={t('billing.created_at')} sortable style={{width: '12em', minWidth: '12em'}}/>
                        <Column header={t('table_headers.action')} body={actionTemplate} style={{width: '11em', minWidth: '11em'}}/>
                    </DataTable>
                </div>
            </div>
        }
        {showAddSiteDefinition &&
            <Dialog visible={true} style={{maxWidth: '50vw', minWidth: '20vw'}} modal={true} onHide={() => {setShowAddSiteDefinition(false);}}>
                <SiteDefinitionAdd selectedDefinition={{url: getHostName(selectedWebSite.url),
                                                        spider_name: selectedWebSite.used_base_spider,
                                                        country:"Universal", sector:"Generic"}}
                                   onComplete={(isCancel)=>{
                                        setShowAddSiteDefinition(false);
                                        if (!isCancel)
                                            changeIsActive(selectedWebSite);
                                   }} />
            </Dialog>
        }
        {showAiJobDialog && selectedAiWebsite &&
            <Dialog header={t('ai_job.define_website_spider')} visible={true} style={{maxWidth: '70vw', minWidth: '45vw'}} modal={true}
                    footer={aiJobDialogFooter()}
                    onHide={() => {setShowAiJobDialog(false);}}>
                <div className="p-grid">
                    {aiJobError &&
                        <div className="p-col-12">
                            <span className="p-tag p-tag-danger" style={{whiteSpace: "normal"}}>{aiJobError}</span>
                        </div>
                    }
                    {aiJobLoading &&
                        <div className="p-col-12">
                            <span className="p-tag p-tag-info">{t('ai_job.loading_list')}</span>
                        </div>
                    }
                    <div className="p-col-12 p-md-6">
                        <b>{t('table_headers.site')}</b>
                        <div style={{wordBreak: "break-word"}}>
                            <a href={selectedAiWebsite.url} target="_blank" rel="noopener noreferrer">{selectedAiWebsite.url}</a>
                        </div>
                    </div>
                    <div className="p-col-12 p-md-6">
                        <b>{t('campaign.campaign')}</b>
                        <div>{selectedAiWebsite.campaign_name || "-"}</div>
                    </div>
                    <div className="p-col-12 p-md-6">
                        <b>{t('user.name')}</b>
                        <div>{selectedAiWebsite.user_name || "-"}</div>
                    </div>
                    <div className="p-col-12 p-md-6">
                        <b>{t('user.email')}</b>
                        <div>{selectedAiWebsite.user_email || "-"}</div>
                    </div>
                    <div className="p-col-12 p-md-6">
                        <b>{t('campaign.competitors.base_spider')}</b>
                        <div>{selectedAiWebsite.used_base_spider || "-"}</div>
                    </div>
                    <div className="p-col-12 p-md-6">
                        <b>{t('ai_job.status')}</b>
                        <div>{statusTemplate(getSelectedWebsiteAiJob())}</div>
                    </div>
                    <div className="p-col-12">
                        <b>{t('ai_job.sample_product_urls')}</b>
                        {getSampleProductUrls(selectedAiWebsite).length === 0
                            ? <div style={{marginTop: ".5em"}}><span className="p-tag p-tag-warning" style={{whiteSpace: "normal"}}>{t('ai_job.sample_urls_required')}</span></div>
                            : <ul style={{marginTop: ".5em"}}>
                                {getSampleProductUrls(selectedAiWebsite).map((url, index) =>
                                    <li key={index} style={{wordBreak: "break-word"}}>
                                        <a href={url} target="_blank" rel="noopener noreferrer">{url}</a>
                                    </li>
                                )}
                            </ul>}
                    </div>
                    {hasActiveAiJob(getSelectedWebsiteAiJob()) &&
                        <div className="p-col-12">
                            <span className="p-tag p-tag-warning" style={{whiteSpace: "normal"}}>{t('ai_job.active_job_exists')}</span>
                        </div>
                    }
                    {getSelectedWebsiteAiJob() &&
                        <div className="p-col-12">
                            <div className="p-grid">
                                <div className="p-col-12 p-md-4">
                                    <b>{t('ai_job.details')}</b>
                                    <div>{getSelectedWebsiteAiJob().id}</div>
                                </div>
                                <div className="p-col-12 p-md-4">
                                    <b>{t('ai_job.provider')}</b>
                                    <div>{getSelectedWebsiteAiJob().provider || "-"}</div>
                                </div>
                                <div className="p-col-12 p-md-4">
                                    <b>{t('ai_job.updated_at')}</b>
                                    <div>{getSelectedWebsiteAiJob().updated_at ? toDateTimeString(getSelectedWebsiteAiJob().updated_at) : "-"}</div>
                                </div>
                                {getSelectedWebsiteAiJob().pull_request_url &&
                                    <div className="p-col-12">
                                        <Button label={t('actions.open_pr')} icon="pi pi-external-link" className="p-button-success"
                                                onClick={() => window.open(getSelectedWebsiteAiJob().pull_request_url, "_blank", "noopener,noreferrer")}
                                                style={{marginRight: ".5em"}}/>
                                        <Button label={t('ai_job.go_to_ai_job_details')} icon={aiJobLoading ? "pi pi-spin pi-spinner" : "pi pi-info-circle"} className="p-button-outlined p-button-info"
                                                disabled={aiJobLoading}
                                                onClick={refreshSelectedAiJobDetails}/>
                                    </div>
                                }
                                {!getSelectedWebsiteAiJob().pull_request_url &&
                                    <div className="p-col-12">
                                        <Button label={t('ai_job.go_to_ai_job_details')} icon={aiJobLoading ? "pi pi-spin pi-spinner" : "pi pi-info-circle"} className="p-button-outlined p-button-info"
                                                disabled={aiJobLoading}
                                                onClick={refreshSelectedAiJobDetails}/>
                                    </div>
                                }
                            </div>
                        </div>
                    }
                    <div className="p-col-12 p-md-6">
                        <label htmlFor="add_website_ai_provider">{t('ai_job.provider')}</label>
                        <Dropdown id="add_website_ai_provider"
                                  value={selectedProvider}
                                  options={getProviderOptions()}
                                  optionDisabled="disabled"
                                  onChange={(e) => {
                                      setSelectedProvider(e.value);
                                      const provider = addWebsiteOperation && addWebsiteOperation.providers ? addWebsiteOperation.providers.find(item => item.name === e.value) : null;
                                      setSelectedModel(getDefaultModelName(provider));
                                  }}
                                  disabled={operationsLoading}
                                  style={{width: "100%", marginTop: ".25em"}}/>
                    </div>
                    <div className="p-col-12 p-md-6">
                        <label htmlFor="add_website_ai_model">{t('ai_job.model')}</label>
                        <Dropdown id="add_website_ai_model"
                                  value={selectedModel}
                                  options={getModelOptions()}
                                  onChange={(e) => setSelectedModel(e.value)}
                                  disabled={operationsLoading || getModelOptions().length === 0}
                                  style={{width: "100%", marginTop: ".25em"}}/>
                    </div>
                </div>
            </Dialog>
        }
        {showProbeDialog && selectedProbeWebsite &&
            <Dialog header={t('request_probe.title')} visible={true} style={{maxWidth: '75vw', minWidth: '50vw'}} modal={true}
                    footer={probeDialogFooter()}
                    onHide={() => {setShowProbeDialog(false);}}>
                <div className="p-grid">
                    <div className="p-col-12 p-md-6">
                        <b>{t('table_headers.site')}</b>
                        <div style={{wordBreak: "break-word"}}>
                            <a href={selectedProbeWebsite.url} target="_blank" rel="noopener noreferrer">{selectedProbeWebsite.url}</a>
                        </div>
                    </div>
                    <div className="p-col-12 p-md-6">
                        <b>{t('campaign.campaign')}</b>
                        <div>{selectedProbeWebsite.campaign_name || "-"}</div>
                    </div>
                    <div className="p-col-12 p-md-5">
                        <label htmlFor="probe_sample_url">{t('ai_job.sample_product_urls')}</label>
                        <Dropdown id="probe_sample_url"
                                  value={selectedProbeSampleUrl}
                                  options={getSampleProductUrls(selectedProbeWebsite).map(url => ({label: url, value: url}))}
                                  onChange={(e) => setSelectedProbeSampleUrl(e.value)}
                                  editable={true}
                                  placeholder={t('request_probe.enter_or_select_sample_url')}
                                  style={{width: "100%", marginTop: ".25em"}}/>
                    </div>
                    <div className="p-col-12 p-md-3">
                        <label htmlFor="probe_request_type">{t('request_probe.request_type')}</label>
                        <Dropdown id="probe_request_type"
                                  value={selectedProbeRequestType}
                                  options={probeRequestTypeOptions}
                                  onChange={(e) => setSelectedProbeRequestType(e.value || "product_url")}
                                  style={{width: "100%", marginTop: ".25em"}}/>
                    </div>
                    <div className="p-col-12 p-md-4">
                        <label htmlFor="probe_proxy_country">{t('request_probe.proxy_country_optional')}</label>
                        <Dropdown id="probe_proxy_country"
                                  value={probeProxyCountry}
                                  options={proxyCountryOptions}
                                  onChange={(e) => setProbeProxyCountry(e.value || "")}
                                  showClear={true}
                                  style={{width: "100%", marginTop: ".25em"}}/>
                    </div>
                    <div className="p-col-12 p-md-6">
                        <label htmlFor="probe_selected_proxies">{t('request_probe.selected_proxies')}</label>
                        <MultiSelect id="probe_selected_proxies"
                                     value={selectedProbeProxies}
                                     options={probeProxyOptions}
                                     onChange={(e) => setSelectedProbeProxies(e.value || [])}
                                     filter={true}
                                     maxSelectedLabels={2}
                                     style={{width: "100%", marginTop: ".25em"}}/>
                    </div>
                    <div className="p-col-12 p-md-6">
                        <label htmlFor="probe_selected_base_spiders">{t('request_probe.selected_base_spiders')}</label>
                        <MultiSelect id="probe_selected_base_spiders"
                                     value={selectedProbeBaseSpiders}
                                     options={probeBaseSpiderOptions}
                                     onChange={(e) => setSelectedProbeBaseSpiders(e.value || [])}
                                     filter={true}
                                     maxSelectedLabels={2}
                                     style={{width: "100%", marginTop: ".25em"}}/>
                    </div>
                    <div className="p-col-12">
                        {selectedProbeSampleUrl ? probeStateTemplate(selectedProbeSampleUrl) : <span className="p-tag p-tag-warning">{t('ai_job.sample_urls_required')}</span>}
                    </div>
                </div>
            </Dialog>
        }
    </React.Fragment>;
};
export default NewWebsites;
